import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  Button,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  IconButton,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { StaticDatePicker } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteIcon from "@mui/icons-material/Delete";

import { getOrdinalSuffix } from "../../services/helper";
import dayjs from "dayjs";
export default function RepeatDate({ formData, handleChange }) {
  const [open, setOpen] = useState(false);
  const [repeatOption, setRepeatOption] = useState("doesNotRepeat");
  const [weekDays, setWeekDays] = useState([]);
  const [otherWeekDays, setOtherWeekDays] = useState([]);
  const [selectedDate, setSelectedDate] = useState();
  const [selectedYearDate, setSelectedYearDate] = useState();
  const [selectedRepeatDates, setSelectedRepeatDates] = useState([]);
  const [sameDay, setSameDay] = useState(false);
  const weekDayMap = {
    Su: "Sunday",
    Mo: "Monday",
    Tu: "Tuesday",
    We: "Wednesday",
    Th: "Thursday",
    Fr: "Friday",
    Sa: "Saturday",
  };
  useEffect(() => {
    if (formData.dateRange[0] && formData.dateRange[1]) {
      const startDate = dayjs(formData.dateRange[0]);
      const endDate = dayjs(formData.dateRange[1]);
      const diff = endDate.diff(startDate, "day");
      if (diff > 0) {
        setSameDay(false);
      } else {
        setSameDay(true);
      }
    }
  }, [formData.dateRange]);
  const handleToggleDays = (event, newDay) => {
    setWeekDays(newDay);
  };

  const handleToggleOtherDays = (event, newDay) => {
    setOtherWeekDays(newDay);
  };
  // Display summary text based on the selection
  const getSummaryText = () => {
    let summary;
    if (repeatOption === "doesNotRepeat") summary = "Does not repeat";
    if (repeatOption === "everyDay") summary = "Every day";
    if (repeatOption === "everyWeek") {
      if (weekDays.length > 0) {
        const dayNames = weekDays.map((day) => weekDayMap[day]).join(", ");
        summary = `Every week on ${dayNames}`;
      } else {
        summary = "";
      }
    }
    if (repeatOption === "everyOtherWeek")
      if (weekDays.length > 0) {
        const dayNames = otherWeekDays.map((day) => weekDayMap[day]).join(", ");
        summary = `Every other week on ${dayNames}`;
      } else {
        summary = "";
      }
    if (repeatOption === "everyMonth") {
      const currentDate = selectedDate?.date() || 1;
      summary = `Every month on the ${currentDate + getOrdinalSuffix(currentDate)}`;
    }
    if (repeatOption === "everyYear")
      summary = `Every year on ${dayjs(selectedDate).format("D MMMM")}`;
    if (repeatOption === "custom") {
      const dayNames = selectedRepeatDates.map((day) =>
        dayjs(day).format("D MMMM"),
      );
      summary = dayNames.join(", ");
    }
    handleChange({ target: { name: "repeatDate", value: summary } });
  };

  const handleRepeatDatesChange = (newValue) => {
    if (!newValue || selectedRepeatDates.includes(newValue)) return;
    setSelectedRepeatDates((prev) => [...prev, newValue]);
    // const syntheticEvent = {
    //   target: {
    //     name: "dateRange",
    //     value: [...selectedRepeatDates, newValue],
    //   },
    // };
    // handleChange(syntheticEvent);
  };

  const handleRemoveDate = (dateToRemove) => {
    setSelectedRepeatDates((prev) =>
      prev.filter((date) => date !== dateToRemove),
    );
  };
  return (
    <>
      {/* Trigger Component */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => setOpen(true)}
        sx={{
          padding: "20px 4px",
          borderRadius: "8px",
          cursor: "pointer",
          width: "100%",
        }}
      >
        <Typography sx={{ color: "gray" }}>Repeats</Typography>
        <Box display="flex" alignItems="center">
          <Typography color="textSecondary" sx={{ textAlign: "end" }}>
            {formData.repeatDate}
          </Typography>
          <ChevronRightIcon sx={{ marginLeft: "8px", color: "gray" }} />
        </Box>
      </Box>

      {/* Dialog Component */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle>Repeats</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={repeatOption}
              onChange={(event) => setRepeatOption(event.target.value)}
            >
              <FormControlLabel
                value="doesNotRepeat"
                control={<Radio />}
                label="Does not repeat"
              />
              {sameDay && (
                <FormControlLabel
                  value="everyDay"
                  control={<Radio />}
                  label="Every day"
                />
              )}

              <FormControlLabel
                value="everyWeek"
                control={<Radio />}
                label={<Box>Every week on</Box>}
              />
              {repeatOption === "everyWeek" && (
                <ToggleButtonGroup
                  value={weekDays}
                  onChange={handleToggleDays}
                  size="small"
                  sx={{ marginLeft: "30px" }}
                >
                  {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                    <ToggleButton
                      value={day}
                      key={day}
                      sx={{
                        border: "1px solid #ccc",
                        borderRadius: "50%",
                        width: "36px",
                        height: "36px",
                        fontSize: "0.9rem",
                        color: "#555",
                        "&.Mui-selected": {
                          backgroundColor: "#1976d2",
                          color: "#fff",
                        },
                        "&:hover": {
                          backgroundColor: "#e3f2fd",
                        },
                      }}
                    >
                      {day}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
              <FormControlLabel
                value="everyOtherWeek"
                control={<Radio />}
                label="Every other week on"
              />
              {repeatOption === "everyOtherWeek" && (
                <ToggleButtonGroup
                  value={otherWeekDays}
                  onChange={handleToggleOtherDays}
                  size="small"
                  sx={{ marginLeft: "30px" }}
                >
                  {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                    <ToggleButton
                      value={day}
                      key={day}
                      sx={{
                        border: "1px solid #ccc",
                        borderRadius: "50%",
                        width: "36px",
                        height: "36px",
                        fontSize: "0.9rem",
                        color: "#555",
                        "&.Mui-selected": {
                          backgroundColor: "#1976d2",
                          color: "#fff",
                        },
                        "&:hover": {
                          backgroundColor: "#e3f2fd",
                        },
                      }}
                    >
                      {day}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
              <FormControlLabel
                value="everyMonth"
                control={<Radio />}
                label="Every month on"
              />
              {repeatOption === "everyMonth" && (
                <Box mt={2}>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="en-gb"
                  >
                    <StaticDatePicker
                      value={selectedDate}
                      onChange={(newDate) => setSelectedDate(newDate)}
                      renderInput={(params) => null}
                      slots={{
                        actionBar: () => null, // Removes the Cancel and OK buttons
                        toolbar: () => null,
                        calendarHeader: () => null,
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              )}
              <FormControlLabel
                value="everyYear"
                control={<Radio />}
                label="Every year on "
              />

              {repeatOption === "everyYear" && (
                <Box mt={2}>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="en-gb"
                  >
                    <StaticDatePicker
                      value={selectedYearDate}
                      onChange={(newDate) => setSelectedYearDate(newDate)}
                      renderInput={(params) => null}
                      slots={{
                        actionBar: () => null, // Removes the Cancel and OK buttons
                        toolbar: () => null,
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              )}

              <FormControlLabel
                value="custom"
                control={<Radio />}
                label="Custom "
              />
              {repeatOption === "custom" && (
                <>
                  <Box mt={2} ml={4}>
                    <Typography variant="body1">Selected Dates:</Typography>
                    {selectedRepeatDates.length > 0 ? (
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {selectedRepeatDates.map((date, index) => (
                          <Box
                            key={index}
                            sx={{
                              padding: "4px 8px",
                              backgroundColor: "#f0f0f0",
                              borderRadius: "4px",
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <Typography>
                              {dayjs(date).format("DD/MM/YYYY")}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveDate(date)}
                              sx={{ marginLeft: "4px" }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2">No dates selected</Typography>
                    )}
                  </Box>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="en-gb"
                  >
                    <StaticDatePicker
                      value={
                        selectedRepeatDates.length
                          ? selectedRepeatDates[selectedRepeatDates.length - 1]
                          : null
                      }
                      onChange={handleRepeatDatesChange}
                      renderInput={(params) => <TextField {...params} />}
                      slots={{
                        actionBar: () => null,
                        toolbar: () => null,
                      }}
                      multiple
                    />
                  </LocalizationProvider>
                </>
              )}
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <Box p={2} display="flex" justifyContent="space-between">
          <Button onClick={() => setOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              console.log("Selected Option:", repeatOption);
              getSummaryText();
              setOpen(false);
            }}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </Box>
      </Dialog>
    </>
  );
}
